import React, { useState, useEffect, useRef } from 'react'
import Map from '../components/Map'
import DeviceSimulator from '../components/DeviceSimulator'
import AIAssistant from '../components/AIAssistant'
import ActiveDevices from '../components/ActiveDevices'
import SensorStatus from '../components/SensorStatus'
import { useSensors } from '../hooks/useSensors'
import { io } from 'socket.io-client'
import { API_BASE } from '../utils/api';

const Dashboard = ({ user, profile }) => {
  const [userLocation, setUserLocation] = useState(null)
  const [nearbyUsers, setNearbyUsers] = useState([])
  const [alerts, setAlerts] = useState([])
  const [activeDevices, setActiveDevices] = useState([])
  const [currentTrip, setCurrentTrip] = useState(null)
  const [collisionAlerts, setCollisionAlerts] = useState([])
  const [backendStatus, setBackendStatus] = useState('checking')
  const socketRef = useRef(null)

  // Use sensor hook for enhanced positioning
  const { 
    sensors, 
    permission, 
    getEnhancedLocation, 
    requestPermission 
  } = useSensors()

  // Get token safely
  const getToken = () => {
    try {
      return localStorage.getItem('safenav_token')
    } catch (error) {
      console.error('Error getting token:', error)
      return null
    }
  }

  useEffect(() => {
    initializeApp()
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect()
      }
    }
  }, [])

  useEffect(() => {
    if (profile) {
      setActiveDevices([
        {
          id: user.id,
          name: profile.username || user.email.split('@')[0],
          type: profile.vehicle_type || 'Personal',
          isActive: true,
          battery: 100,
          speed: userLocation?.speed || 0,
          location: userLocation || { latitude: 10.8665, longitude: 76.8836 },
          sensors: {
            gps: !!sensors.gps,
            accelerometer: !!sensors.accelerometer,
            gyroscope: !!sensors.gyroscope,
            wifi: !!sensors.wifi
          }
        }
      ])
    }
  }, [profile, user, userLocation, sensors])

  const initializeApp = async () => {
    // Test backend connection
    await testBackendConnection()
    // Initialize WebSocket connection
    socketRef.current = io('http://localhost:3001')
    // Join user room
    const token = getToken()
    if (token) {
      socketRef.current.emit('join', user.id)
    }
    // Start enhanced location tracking with sensors
    startEnhancedLocationTracking()
    // Subscribe to real-time updates
    socketRef.current.on('location_broadcast', (data) => {
      setNearbyUsers(prev => {
        const filtered = prev.filter(u => u.userId !== data.userId)
        return [...filtered, { ...data, sensorEnhanced: true }]
      })
    })
    // Listen for collision alerts
    socketRef.current.on('collision_alert', (data) => {
      setCollisionAlerts(prev => [...prev, { ...data, id: Date.now() }])
    })
  }

  const startEnhancedLocationTracking = () => {
    if (navigator.geolocation) {
      const options = {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
      const watchId = navigator.geolocation.watchPosition(
        (position) => {
          // Get enhanced location using sensor fusion
          const enhancedLocation = getEnhancedLocation() || {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            speed: position.coords.speed ? position.coords.speed * 3.6 : 0,
            heading: position.coords.heading || 0,
            accuracy: position.coords.accuracy,
            altitude: position.coords.altitude,
            timestamp: new Date()
          }
          setUserLocation(enhancedLocation)
          sendLocationToBackend(enhancedLocation)
          if (socketRef.current) {
            socketRef.current.emit('location_update', enhancedLocation)
          }
          fetchNearbyUsers(enhancedLocation.latitude, enhancedLocation.longitude)
        },
        (error) => {
          console.log('GPS error, using sensor-enhanced fallback:', error)
          const sensorLocation = getEnhancedLocation()
          if (sensorLocation) {
            setUserLocation(sensorLocation)
          } else {
            setUserLocation({ 
              latitude: 10.8665, 
              longitude: 76.8836, 
              speed: 0,
              accuracy: 1000 
            })
          }
        },
        options
      )
      return () => navigator.geolocation.clearWatch(watchId)
    }
  }

  const testBackendConnection = async () => {
    try {
      const response = await fetch('http://localhost:3001/api/health')
      if (response.ok) {
        setBackendStatus('connected')
        console.log('✅ Backend connection successful')
      } else {
        setBackendStatus('error')
        console.error('❌ Backend connection failed')
      }
    } catch (error) {
      setBackendStatus('error')
      console.error('❌ Backend connection error:', error)
    }
  }

  const sendLocationToBackend = async (location) => {
    try {
      const token = getToken()
      if (!token) return
      await fetch('http://localhost:3001/api/location', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          latitude: location.latitude,
          longitude: location.longitude,
          speed: location.speed,
          heading: location.heading,
          accuracy: location.accuracy,
          sensor_data: {
            accelerometer: sensors.accelerometer,
            gyroscope: sensors.gyroscope,
            enhanced: true
          }
        })
      })
    } catch (error) {
      console.error('Error sending location:', error)
    }
  }

  const fetchNearbyUsers = async (lat, lng) => {
    try {
      const token = getToken()
      if (!token) return
      const response = await fetch(`http://localhost:3001/api/nearby-users?latitude=${lat}&longitude=${lng}&radius=500`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      if (response.ok) {
        const users = await response.json()
        setNearbyUsers(users)
      }
    } catch (error) {
      console.error('Error fetching nearby users:', error)
    }
  }

  const handleStartTrip = async (deviceData) => {
    try {
      const token = getToken()
      if (!token) {
        throw new Error('No authentication token found. Please log out and log in again.')
      }
      const response = await fetch('http://localhost:3001/api/trips/start', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          vehicle_name: deviceData.name,
          start_location: {
            latitude: userLocation?.latitude || 10.8665,
            longitude: userLocation?.longitude || 76.8836,
            timestamp: new Date().toISOString(),
            sensor_enhanced: true
          }
        })
      })
      if (response.status === 401) {
        throw new Error('Authentication failed. Please log in again.')
      }
      if (!response.ok) {
        throw new Error(`Failed to start trip: ${response.status}`)
      }
      const trip = await response.json()
      setCurrentTrip(trip)
      setAlerts(prev => [...prev, {
        id: Date.now(),
        type: 'trip_started',
        message: `Trip started with ${deviceData.name} 🚀 (Sensor Enhanced)`,
        timestamp: new Date()
      }])
    } catch (error) {
      console.error('❌ Error starting trip:', error)
      setAlerts(prev => [...prev, {
        id: Date.now(),
        type: 'error',
        message: error.message,
        timestamp: new Date()
      }])
    }
  }

  const handleStopTrip = async () => {
    try {
      const token = getToken()
      if (!token) {
        throw new Error('No authentication token found.')
      }
      const response = await fetch('http://localhost:3001/api/trips/end', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          end_location: {
            latitude: userLocation?.latitude || 10.8665,
            longitude: userLocation?.longitude || 76.8836,
            timestamp: new Date().toISOString(),
            sensor_enhanced: true
          }
        })
      })
      if (!response.ok) {
        throw new Error(`Failed to end trip: ${response.status}`)
      }
      const trip = await response.json()
      setCurrentTrip(null)
      setAlerts(prev => [...prev, {
        id: Date.now(),
        type: 'trip_ended',
        message: `Trip completed! Distance: ${trip.distance || 0}km (Sensor Enhanced) 🌟`,
        timestamp: new Date()
      }])
    } catch (error) {
      console.error('Error ending trip:', error)
      setAlerts(prev => [...prev, {
        id: Date.now(),
        type: 'error',
        message: error.message,
        timestamp: new Date()
      }])
    }
  }

  const getLocationAccuracy = () => {
    if (!userLocation) return 'Unknown'
    const baseAccuracy = userLocation.accuracy || 1000
    let enhancedAccuracy = baseAccuracy
    if (sensors.accelerometer) {
      const movement = Math.sqrt(
        sensors.accelerometer.x ** 2 + 
        sensors.accelerometer.y ** 2 + 
        sensors.accelerometer.z ** 2
      )
      if (movement > 0.3) {
        enhancedAccuracy = Math.max(5, baseAccuracy * 0.6)
      }
    }
    if (sensors.wifi) {
      enhancedAccuracy = Math.max(3, enhancedAccuracy * 0.8)
    }
    return enhancedAccuracy.toFixed(1)
  }

  const getSensorStatusLevel = () => {
    const activeSensors = [
      sensors.gps,
      sensors.accelerometer,
      sensors.gyroscope,
      sensors.wifi
    ].filter(Boolean).length
    if (activeSensors >= 3) return 'optimal'
    if (activeSensors >= 2) return 'good'
    if (activeSensors >= 1) return 'basic'
    return 'poor'
  }

  const sensorStatus = getSensorStatusLevel()

  return (
    <div className="space-y-6">
      {/* Backend Status */}
      {backendStatus === 'error' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-red-800">Backend Connection Issue</h3>
          <p className="text-red-700">Cannot connect to the server. Make sure backend is running on port 3001.</p>
        </div>
      )}

      {/* AI Assistant */}
      <AIAssistant 
        alerts={alerts} 
        collisionAlerts={collisionAlerts} 
      />
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Map and Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Enhanced Status Banner */}
          <div className={`rounded-lg p-4 ${
            sensorStatus === 'optimal' ? 'bg-green-50 border border-green-200' :
            sensorStatus === 'good' ? 'bg-blue-50 border border-blue-200' :
            sensorStatus === 'basic' ? 'bg-yellow-50 border border-yellow-200' :
            'bg-red-50 border border-red-200'
          }`}>
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-800">
                  {sensorStatus === 'optimal' ? 'Multi-Sensor Tracking Active' : 
                   sensorStatus === 'good' ? 'Enhanced Tracking' : 
                   sensorStatus === 'basic' ? 'Basic Tracking' : 
                   'Limited Tracking'}
                </h2>
                <p className="text-gray-600 text-sm">
                  GPS + Motion Sensors + WiFi Positioning
                </p>
                <div className="flex items-center space-x-4 mt-2 text-xs">
                  <span className={`px-2 py-1 rounded ${
                    sensors.gps ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    📍 GPS: {sensors.gps ? 'Active' : 'Inactive'}
                  </span>
                  <span className={`px-2 py-1 rounded ${
                    sensors.accelerometer ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    📊 Motion: {sensors.accelerometer ? 'Active' : 'Inactive'}
                  </span>
                  <span className={`px-2 py-1 rounded ${
                    sensors.wifi ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    📶 WiFi: {sensors.wifi ? 'Active' : 'Inactive'}
                  </span>
                </div>
                {userLocation && (
                  <p className="text-green-500 text-xs mt-1">
                    Enhanced Accuracy: ±{getLocationAccuracy()}m
                  </p>
                )}
              </div>
              <div className="text-right">
                {activeDevices.map(device => (
                  <div key={device.id} className="text-sm text-gray-700">
                    <strong>{device.name}</strong><br />
                    {userLocation ? (
                      <>
                        {userLocation.latitude.toFixed(6)}, {userLocation.longitude.toFixed(6)}<br />
                        Speed: {userLocation.speed ? userLocation.speed.toFixed(1) : 0} km/h
                        {userLocation.sensorEnhanced && ' 🚀'}
                      </>
                    ) : (
                      'Calibrating sensors...'
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sensor Status Component */}
          <SensorStatus 
            sensors={sensors}
            permission={permission}
            onRequestPermissions={requestPermission}
          />

          {/* Collision Alerts */}
          {collisionAlerts.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-red-800 mb-2">
                🚨 Active Collision Alerts
              </h3>
              {collisionAlerts.slice(0, 3).map((alert, index) => (
                <div key={index} className="text-sm text-red-700 mb-1">
                  {alert.collisions?.[0]?.message}
                  {alert.sensorEnhanced && ' (Sensor Enhanced)'}
                </div>
              ))}
            </div>
          )}

          {/* Map */}
          <div className="bg-white rounded-lg shadow-sm border h-96">
            <Map 
              userLocation={userLocation}
              nearbyUsers={nearbyUsers}
              alerts={alerts}
              sensorEnhanced={sensorStatus !== 'poor'}
            />
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Device Simulator */}
          <DeviceSimulator 
            onStartTrip={handleStartTrip}
            onStopTrip={handleStopTrip}
            currentTrip={currentTrip}
            userProfile={profile}
            sensorStatus={sensorStatus}
          />

          {/* Active Devices */}
          <ActiveDevices devices={activeDevices} />

          {/* Nearby Users Panel */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Nearby Users ({nearbyUsers.length})
            </h3>
            <div className="space-y-3 max-h-60 overflow-y-auto">
              {nearbyUsers.map((user, index) => (
                <div
                  key={user.id || index}
                  className={`p-3 border rounded-lg ${
                    user.risk_level === 'high' 
                      ? 'bg-red-50 border-red-200' 
                      : user.risk_level === 'medium'
                      ? 'bg-yellow-50 border-yellow-200'
                      : 'bg-green-50 border-green-200'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-medium text-gray-900">{user.username || 'Nearby User'}</h4>
                      <p className="text-sm text-gray-600 capitalize">{user.vehicle_type || 'Unknown'}</p>
                    </div>
                    <div className="text-right">
                      <span className={`text-xs font-medium px-2 py-1 rounded ${
                        user.risk_level === 'high' 
                          ? 'bg-red-100 text-red-800' 
                          : user.risk_level === 'medium'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {user.risk_level || 'low'}
                      </span>
                      {user.sensorEnhanced && (
                        <div className="text-xs text-blue-600 mt-1">🚀 Enhanced</div>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Distance: {user.distance ? `${user.distance}m` : 'Unknown'} • 
                    Speed: {user.speed ? user.speed.toFixed(1) : 0} km/h
                  </p>
                </div>
              ))}
              {nearbyUsers.length === 0 && (
                <p className="text-gray-500 text-center py-4">
                  No nearby users detected
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Dashboard
